import React, { useState } from "react";
import { Card, CardContent, Typography, Box, IconButton, Collapse } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import { fontFamily, useSharedValue } from "../Constants/constants";

function Notifications() {
  const { notifications, updateNotificationWindowValue, handleNotificationList } = useSharedValue();
  const [expandedNotifications, setExpandedNotifications] = useState({});

  const formatDate = (timestamp) => {
    // Handle invalid or missing timestamps
    if (!timestamp) {
      return {
        formattedDate: "Just now",
        formattedTime: "",
      };
    }

    const date = new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid notification timestamp:", timestamp);
      return {
        formattedDate: "Just now",
        formattedTime: "",
      };
    }

    return {
      formattedDate: date.toLocaleDateString(),
      formattedTime: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
  };

  const toggleNotificationDetails = (notificationId) => {
    setExpandedNotifications((prev) => ({
      ...prev,
      [notificationId]: !prev[notificationId],
    }));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircleOutlineIcon />;
      case "error":
        return <ErrorOutlineIcon />;
      case "warning":
        return <WarningAmberIcon />;
      case "info":
        return <InfoOutlinedIcon />;
      default:
        return <InfoOutlinedIcon />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "success":
        return "#10b981";
      case "error":
        return "#ef4444";
      case "warning":
        return "#f59e0b";
      case "info":
        return "#3b82f6";
      default:
        return "#3b82f6";
    }
  };

  const toggleChatWindow = () => {
    updateNotificationWindowValue(false);
  };
  const clearAllNotifications = () => {
    handleNotificationList([]);
    localStorage.removeItem("notifications_list");
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        right: 0,
        width: "380px",
        height: "calc(100vh - 66px)",
        backgroundColor: "#ffffff",
        borderRadius: "12px 0 0 0",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        zIndex: 100001,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          background: "var(--application-secondary-color)",
          color: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NotificationsIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontFamily: fontFamily, fontSize: 18, fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* Clear All Text */}
          {notifications.length > 0 && (
            <Typography
              variant="body2"
              onClick={clearAllNotifications}
              sx={{
                cursor: "pointer",
                color: "#ffffff",
                fontSize: 13,
                fontWeight: 500,
                padding: "4px 12px",
                borderRadius: "6px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Clear All
            </Typography>
          )}
          {/* Close Notifications Window */}
          <IconButton onClick={toggleChatWindow} size="small">
            <CloseIcon style={{ color: "#ffffff" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Notifications List */}
      <Box
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          padding: "16px 12px",
          backgroundColor: "#f8f9fa",
        }}
      >
        {notifications.length > 0 ? (
          notifications
            .slice()
            .reverse()
            .map((notification, index) => {
              // Support both old and new notification formats
              const notificationId = notification.id || `notification-${index}`;
              const notificationType = notification.type || "info";
              const notificationTitle =
                notification.title || notification.filename || "Notification";
              const notificationDescription =
                notification.description || notification.message || "";
              const notificationCreatedAt = notification.createdAt || notification.date;
              const notificationMoreDetails = notification.moreDetails;
              const isExpanded = expandedNotifications[notificationId];

              const { formattedDate, formattedTime } = formatDate(notificationCreatedAt);
              const hasMoreDetails =
                notificationMoreDetails && notificationMoreDetails.trim().length > 0;

              return (
                <Card
                  key={notificationId}
                  sx={{
                    marginBottom: "12px",
                    borderRadius: "12px",
                    background: "#ffffff",
                    boxShadow: "none",
                    border: `1px solid ${getNotificationColor(notificationType)}20`,
                    transition: "all 0.2s ease",
                    overflow: "hidden",
                    "&:hover": {
                      boxShadow: "none",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "16px",
                      "&:last-child": { paddingBottom: "16px" },
                    }}
                  >
                    {/* Main Notification Content */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {/* Icon with colored background */}
                      <Box
                        sx={{
                          minWidth: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: `${getNotificationColor(notificationType)}15`,
                        }}
                      >
                        {React.cloneElement(getNotificationIcon(notificationType), {
                          sx: {
                            color: getNotificationColor(notificationType),
                            fontSize: 24,
                            margin: 0,
                          },
                        })}
                      </Box>

                      {/* Notification Details */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            fontSize: "15px",
                            fontFamily: fontFamily,
                            color: "#1a1a1a",
                            lineHeight: 1.4,
                            marginBottom: "4px",
                          }}
                        >
                          {notificationTitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            color: "#666666",
                            fontFamily: fontFamily,
                            lineHeight: 1.5,
                            marginBottom: "8px",
                          }}
                        >
                          {notificationDescription}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 500,
                              fontSize: "11px",
                              fontFamily: fontFamily,
                              color: "#999999",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {formattedTime ? `${formattedDate} at ${formattedTime}` : formattedDate}
                          </Typography>

                          {/* Show More Details Button */}
                          {hasMoreDetails && (
                            <IconButton
                              size="small"
                              onClick={() => toggleNotificationDetails(notificationId)}
                              sx={{
                                padding: "4px",
                                marginLeft: 1,
                                backgroundColor: "#f5f5f5",
                                "&:hover": {
                                  backgroundColor: "#e8e8e8",
                                },
                              }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon sx={{ fontSize: 18, color: "#666" }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 18, color: "#666" }} />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Expandable More Details Section */}
                    {hasMoreDetails && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            marginTop: "12px",
                            paddingTop: "12px",
                            borderTop: "1px solid #e8e8e8",
                            paddingLeft: "8px",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "12px",
                              fontFamily: fontFamily,
                              color: "#666666",
                              display: "block",
                              lineHeight: 1.6,
                              backgroundColor: "#f8f9fa",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              borderLeft: `3px solid ${getNotificationColor(notificationType)}`,
                            }}
                          >
                            <strong style={{ color: "#333" }}>Details:</strong>{" "}
                            {notificationMoreDetails}
                          </Typography>
                        </Box>
                      </Collapse>
                    )}
                  </CardContent>
                </Card>
              );
            })
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              padding: 4,
            }}
          >
            <Box
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                backgroundColor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 3,
              }}
            >
              <NotificationsIcon sx={{ fontSize: 60, color: "#d0d0d0" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: fontFamily,
                fontSize: 16,
                fontWeight: 600,
                color: "#666666",
                marginBottom: 1,
              }}
            >
              No Notifications Yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: fontFamily,
                fontSize: 13,
                color: "#999999",
                maxWidth: "80%",
              }}
            >
              You'll see updates and alerts here when they arrive
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Notifications;
